import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { subscribeToToasts, type Toast } from './toast.bus';
import styles from './toast.module.scss';

/**
 * Renders the active toast stack. Mount once near the root.
 * Auto-dismisses each toast after `toast.durationMs` (skipped when
 * the value is `0`).
 */
export function ToastHost(): JSX.Element {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  useEffect(() => {
    return subscribeToToasts((toast) => {
      setToasts((prev) => [...prev, toast]);
      if (toast.durationMs > 0) {
        window.setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== toast.id));
        }, toast.durationMs);
      }
    });
  }, []);

  const dismiss = (id: number): void => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <div className={styles.host} role="region" aria-label="Notifications" aria-live="polite">
      {toasts.map((t) => (
        <div
          key={t.id}
          role={t.level === 'error' ? 'alert' : 'status'}
          className={clsx(styles.toast, styles[`toast_${t.level}`])}
        >
          <span className={styles.message}>{t.message}</span>
          <button
            type="button"
            className={styles.dismiss}
            aria-label="Dismiss notification"
            onClick={() => dismiss(t.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
